/* eslint-disable complexity, max-lines */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import i18next from 'i18next';
import { QueryClient, UseMutateFunction } from 'react-query';
import { Reporter } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth/RequiredFeatureEnabled';
import { formatDateOrHidden } from 'src/components/helpers/formatDateOrHidden';
import {
  DefaultOption,
  handleErrorToast,
  handleSuccessToast,
  InviteUserDropdownOption,
} from 'src/components/molecules';
import { AccessActionEnum } from 'src/components/molecules/InviteToCaseModal/components/AccessItem/AccessItem';
import { UploadedEvidenceFile } from 'src/components/molecules/UploadedEvidence/types';
import {
  editCaseTeamMemberAccess,
  removeCaseTeamMember,
} from 'src/components/organisms/Case/ReviewCaseTeamMemberModal/ReviewCaseTeamMemberModal.thunks';
import { formatEventEvidence } from 'src/components/organisms/EventDetailsAccordion/formatEventEvidence';
import {
  AccountType,
  AuditEntryType,
  CallRecordingUrlType,
  CaseDocumentationType,
  CaseTeamMemberType,
  CaseType,
  CategoryDisplayType,
  DecryptedEvidenceType,
  Exact,
  FileEvidenceDetailsType,
  GetCaseLinksByCaseIdQuery,
  GetCaseTeamNoteQuery,
  Maybe,
  MiReportSource,
  ParsedEventsType,
  ParsedEvidenceType,
  ReassignReportAssigneeMutation,
  ReassignReportAssigneeMutationVariables,
  RelationshipTypeQuery,
  ReportType,
  RoleType,
  UpdateDeterminedCategoryMutation,
  UpdateDeterminedCategoryMutationVariables,
  UpdateSubmissionDateMutation,
  UpdateSubmissionDateMutationVariables,
  UpdateSubstantiationStatusMutation,
  UpdateSubstantiationStatusMutationVariables,
  useAddExternalNamedOffenderMutation,
  useAddInternalNamedOffenderMutation,
  UserType,
} from 'src/graphql';
import { getActiveCategories } from 'src/reducers/Categories.slice';
import { getOrganisationTags } from 'src/reducers/Tags.slice';
import { selectCurrentUser } from 'src/selectors/selectCurrentUser';
import {
  CasePermissionsType,
  CaseTeamMemberPermissionsType,
  FeatureFlag,
  FormState,
  getReadableIdWithDash,
  MiReportSourceForDisplay,
  Nullable,
  PhoneLineType,
  REPORT_SOURCE,
  State,
  SUBMISSION_MODE,
  TranscriptLanguages,
  UploadScanStatus,
} from 'src/types';
import {
  graphQLReportSourceToLocalReportSource,
  stripFileExtension,
} from 'src/utils';
import { sortObjectByName } from 'src/utils/sortObjectByName';
import { formatDateWithSlashAndShortYear } from '../Cases/helpers/formatDate';
import { mapSubmissionModeToRoute } from './helpers';
import { getMessagingDisabled } from './helpers/getMessagingDisabled';
import { getReadableReportSource } from './helpers/getReadableReportSource';
import { hiddenDots } from './helpers/hiddenDots';
import { isNonEditableNotificationEmail } from './helpers/isNonEditableNotificationEmail';
import { mapCategoriesToDefaultOptions } from './helpers/mapCategoriesToDefaultOptions';
import {
  formatEventNamedOffendersForDisplay,
  formatReportNamedOffendersForDisplay,
  FormattedNamedOffender,
  NamedOffenderForDisplay,
} from './helpers/namedOffender';
import {
  getCaseNamedOffenders,
  getEventNamedOffenders,
  getReportNamedOffenders,
} from './helpers/namedOffender/getNamedOffenders';
import {
  getInputFormat,
  getMiReportSource,
  getNotificationEmailState,
  getReporterDetails,
  getStringFormat,
  getSubmissionDate,
  getSubmissionRoute,
  getSubstantiationStatus,
  isDownloadReportEnabled,
} from './helpers/permissionsConfig';

// Utility function to handle permission updates with PII consistency
export const updatePermissions = (
  currentPermissions: CasePermissionsType[],
  permissionsToUpdate: CasePermissionsType[],
  action: AccessActionEnum,
): CasePermissionsType[] => {
  let updatedPermissions: CasePermissionsType[] = [];

  if (action === AccessActionEnum.ADD) {
    updatedPermissions = [
      ...new Set([...currentPermissions, ...permissionsToUpdate]),
    ];
  } else {
    updatedPermissions = currentPermissions.filter(
      (permission) => !permissionsToUpdate.includes(permission),
    );
  }

  // Handle PII permission dependencies
  if (
    action === AccessActionEnum.ADD &&
    permissionsToUpdate.includes('VIEW_CASE_AUDIT_LOGS_TAB')
  ) {
    // When adding audit logs access, ensure both PII permissions are present
    if (!updatedPermissions.includes('VIEW_CASE_DETAILS_PII')) {
      updatedPermissions.push('VIEW_CASE_DETAILS_PII');
    }
    if (!updatedPermissions.includes('VIEW_REPORT_DETAILS_PII')) {
      updatedPermissions.push('VIEW_REPORT_DETAILS_PII');
    }
  } else if (
    action === AccessActionEnum.REMOVE &&
    permissionsToUpdate.includes('VIEW_CASE_AUDIT_LOGS_TAB')
  ) {
    // When removing audit logs access, also remove all PII permissions
    updatedPermissions = updatedPermissions.filter(
      (permission) =>
        !['VIEW_CASE_DETAILS_PII', 'VIEW_REPORT_DETAILS_PII'].includes(
          permission,
        ),
    );
  } else if (
    action === AccessActionEnum.REMOVE &&
    (permissionsToUpdate.includes('VIEW_CASE_DETAILS_PII') ||
      permissionsToUpdate.includes('VIEW_REPORT_DETAILS_PII'))
  ) {
    // If removing either PII permission, check if audit logs should also be removed
    const hasViewCasePII = updatedPermissions.includes('VIEW_CASE_DETAILS_PII');
    const hasViewReportPII = updatedPermissions.includes(
      'VIEW_REPORT_DETAILS_PII',
    );

    if (!hasViewCasePII || !hasViewReportPII) {
      updatedPermissions = updatedPermissions.filter(
        (permission) => permission !== 'VIEW_CASE_AUDIT_LOGS_TAB',
      );
    }
  } else if (
    action === AccessActionEnum.ADD &&
    (permissionsToUpdate.includes('VIEW_CASE_DETAILS_PII') ||
      permissionsToUpdate.includes('VIEW_REPORT_DETAILS_PII'))
  ) {
    // If adding PII permission, check if we should also add audit logs
    const hasViewCasePII = updatedPermissions.includes('VIEW_CASE_DETAILS_PII');
    const hasViewReportPII = updatedPermissions.includes(
      'VIEW_REPORT_DETAILS_PII',
    );

    if (
      hasViewCasePII &&
      hasViewReportPII &&
      !updatedPermissions.includes('VIEW_CASE_AUDIT_LOGS_TAB')
    ) {
      updatedPermissions.push('VIEW_CASE_AUDIT_LOGS_TAB');
    }
  }

  return [...new Set(updatedPermissions)];
};

type UpdateDeterminedCategoryPayload = {
  mutationUpdateDeterminedCategory: UseMutateFunction<
    UpdateDeterminedCategoryMutation,
    unknown,
    Exact<{
      determinedCategoryId: number;
      report: string;
    }>,
    unknown
  >;
} & UpdateDeterminedCategoryMutationVariables;

export const updateDeterminedCategory = createAsyncThunk(
  'case/updateDeterminedCategory',
  async (
    {
      mutationUpdateDeterminedCategory,
      report,
      determinedCategoryId,
    }: UpdateDeterminedCategoryPayload,
    _thunkAPI,
  ) => {
    const data = await mutationUpdateDeterminedCategory({
      determinedCategoryId,
      report,
    });

    return data;
  },
);

type UpdateReportAssigneePayload = {
  mutationReassignReportAssignee: UseMutateFunction<
    ReassignReportAssigneeMutation,
    unknown,
    Exact<{
      newReportAssigneeId: number;
      report: string;
      caseId: string;
    }>,
    unknown
  >;
} & ReassignReportAssigneeMutationVariables;

export const updateReportAssignee = createAsyncThunk(
  'case/updateReportAssignee',
  async (
    {
      mutationReassignReportAssignee,
      report,
      newReportAssigneeId,
      caseId,
    }: UpdateReportAssigneePayload,
    _thunkAPI,
  ) => {
    const data = await mutationReassignReportAssignee({
      newReportAssigneeId,
      report,
      caseId,
    });

    return data;
  },
);

type UpdateSubstantiationStatusPayload = {
  mutationUpdateSubstantiationStatus: UseMutateFunction<
    UpdateSubstantiationStatusMutation,
    unknown,
    Exact<{
      newSubstantiationStatus: string;
      report: string;
      caseId: string;
    }>,
    unknown
  >;
} & UpdateSubstantiationStatusMutationVariables;

export const updateSubstantiationStatus = createAsyncThunk(
  'case/updateSubstantiationStatus',
  async (
    {
      mutationUpdateSubstantiationStatus,
      report,
      newSubstantiationStatus,
      caseId,
    }: UpdateSubstantiationStatusPayload,
    _thunkAPI,
  ) => {
    const data = await mutationUpdateSubstantiationStatus({
      newSubstantiationStatus,
      report,
      caseId,
    });

    return data;
  },
);

type UpdateSubmissionDatePayload = {
  mutationUpdateSubmissionDate: UseMutateFunction<
    UpdateSubmissionDateMutation,
    unknown,
    Exact<{
      submittedAt: string;
      report: string;
    }>,
    unknown
  >;
} & UpdateSubmissionDateMutationVariables;

export const updateSubmissionDate = createAsyncThunk(
  'case/updateSubmissionDate',
  async (
    {
      mutationUpdateSubmissionDate,
      report,
      submittedAt,
    }: UpdateSubmissionDatePayload,
    _thunkAPI,
  ) => {
    const data = await mutationUpdateSubmissionDate({
      submittedAt,
      report,
    });

    return data;
  },
);

type AddOrLinkNamedOffenderArgs = {
  caseId: string;
  reportId: string;
  eventId: string;
  organisationId: number;
  externalNamedOffenderToLink?: string;
  mutationAddInternalNamedOffender: ReturnType<
    typeof useAddInternalNamedOffenderMutation
  >['mutateAsync'];
  mutationAddExternalNamedOffender: ReturnType<
    typeof useAddExternalNamedOffenderMutation
  >['mutateAsync'];
  queryClient: QueryClient;
  selectedUser: UserType | null;
  externalName: string;
  internal: boolean;
};

export const addOrLinkNamedOffender = createAsyncThunk<
  { message: string },
  AddOrLinkNamedOffenderArgs
>(
  'case/addOrLinkNamedOffender',
  async (
    {
      caseId,
      reportId,
      eventId,
      organisationId,
      selectedUser,
      externalNamedOffenderToLink,
      mutationAddInternalNamedOffender,
      mutationAddExternalNamedOffender,
      queryClient,
      externalName,
      internal,
    }: AddOrLinkNamedOffenderArgs,
    _thunkAPI,
  ) => {
    if (externalNamedOffenderToLink && selectedUser?.id) {
      await mutationAddInternalNamedOffender({
        caseId,
        reportId,
        user: {
          user: selectedUser?.id,
          event: eventId,
          organisation: organisationId,
          linkedPresubmissionOffender: externalNamedOffenderToLink,
        },
      });

      queryClient.invalidateQueries(['CaseById']);
      return { message: 'Named offender linked successfully' };
    }

    if (internal && selectedUser?.id) {
      await mutationAddInternalNamedOffender({
        caseId,
        reportId,
        user: {
          user: selectedUser?.id,
          event: eventId,
          organisation: organisationId,
        },
      });

      queryClient.invalidateQueries(['CaseById']);
      return { message: 'Named offender added successfully' };
    }

    if (!internal && externalName) {
      await mutationAddExternalNamedOffender({
        caseId,
        reportId,
        user: {
          name: externalName,
          event: eventId,
          organisation: organisationId,
        },
      });

      queryClient.invalidateQueries(['CaseById']);
      return { message: 'Named offender added successfully' };
    }
    return { message: '' };
  },
);

export type CaseInitialState = {
  caseById: Nullable<CaseType>;
  translationModalOpenState?: boolean;
  caseSelectedTranslation?: Nullable<TranscriptLanguages>;
  caseTeamNote?: Nullable<GetCaseTeamNoteQuery['getCaseTeamNote']>;
  viewer?: Nullable<UserType>;
  categories?: Nullable<CategoryDisplayType[]>;
  determinedCategories?: Nullable<CategoryDisplayType[]>;
  showAddNamedOffenderModal?: boolean;
  usersToExcludeInNamedOffenderModal?: number[];
  selectedEventId?: string;
  externalNamedOffenderToLink?: string;
  showRemoveCaseMemberModal?: boolean;
  showEditCaseMemberModal?: boolean;
  messagesVisible?: boolean;
  selectedCaseTeamMemberPermissions?: CaseTeamMemberPermissionsType[];
  relationshipTypes?: Nullable<RelationshipTypeQuery['getRelationshipType']>;
  caseLinks?: Nullable<GetCaseLinksByCaseIdQuery['getCaseLinksByCaseId']>;
};

const initialState: CaseInitialState = {
  caseById: null,
  caseSelectedTranslation: null,
  translationModalOpenState: false,
  caseTeamNote: null,
  categories: null,
  determinedCategories: null,
  usersToExcludeInNamedOffenderModal: [],
  viewer: null,
  showAddNamedOffenderModal: false,
  showRemoveCaseMemberModal: false,
  showEditCaseMemberModal: false,
  messagesVisible: false,
  selectedCaseTeamMemberPermissions: [],
};

export const caseSlice = createSlice({
  name: 'case',
  initialState,

  reducers: {
    caseDataUpdated: (state, action: { payload: CaseType | null }) => {
      if (!action.payload || !action.payload.id) {
        return state;
      }

      state.caseById = action.payload;
      return state;
    },
    caseSelectedTranslationUpdated: (
      state,
      action: { payload: TranscriptLanguages | null },
    ) => {
      if (!action.payload) {
        return state;
      }

      state.caseSelectedTranslation = action.payload;
      return state;
    },
    translationModalOpenStateUpdated: (
      state,
      action: { payload: { open: boolean } },
    ) => {
      if (!action.payload) {
        return state;
      }

      state.translationModalOpenState = action.payload.open;
    },
    caseTeamNoteUpdated: (
      state,
      action: { payload: GetCaseTeamNoteQuery['getCaseTeamNote'] },
    ) => {
      state.caseTeamNote = action.payload;

      return state;
    },
    caseCategoriesUpdated: (
      state,
      action: { payload: CategoryDisplayType[] },
    ) => {
      if (!action.payload) {
        return state;
      }

      state.categories = action.payload;
      return state;
    },
    caseDeterminedCategoriesUpdated(
      state,
      action: { payload: CategoryDisplayType[] | null },
    ) {
      if (!action.payload) {
        return state;
      }

      state.determinedCategories = action.payload;
      return state;
    },
    relationshipTypesUpdated(
      state,
      action: { payload: RelationshipTypeQuery['getRelationshipType'] | null },
    ) {
      if (!action.payload) {
        return state;
      }

      state.relationshipTypes = action.payload;
      return state;
    },
    reporterNotificationEmailUpdated(
      state,
      action: { payload: { reportId: string; contactEmail: string } | null },
    ) {
      if (!action.payload) {
        return state;
      }

      state.caseById?.Reports?.forEach((report) => {
        if (report.id === action.payload?.reportId)
          if (report.Reporter) {
            report.Reporter.contactEmail = action.payload.contactEmail;
          }
      });
      return state;
    },
    viewerDataUpdated: (state, action: { payload: UserType | null }) => {
      if (action?.payload?.id) {
        state.viewer = action.payload;
      }
      return state;
    },
    toggleShowAddNamedOffenderModal: (
      state,
      action: {
        payload: {
          open: boolean;
          usersToExclude?: number[];
          reportId?: string;
          eventId?: string;
          externalNamedOffenderToLink?: string;
        };
      },
    ) => {
      state.showAddNamedOffenderModal = action.payload.open;
      state.usersToExcludeInNamedOffenderModal =
        action.payload.usersToExclude ?? [];
      state.selectedEventId = action.payload.eventId;
      state.externalNamedOffenderToLink =
        action.payload.externalNamedOffenderToLink;
      return state;
    },
    toggleShowRemoveCaseMemberModal: (
      state,
      action: {
        payload: {
          open: boolean;
        };
      },
    ) => {
      state.showRemoveCaseMemberModal = action.payload.open;
      return state;
    },
    toggleShowEditCaseMemberModal: (
      state,
      action: {
        payload: {
          open: boolean;
        };
      },
    ) => {
      state.showEditCaseMemberModal = action.payload.open;
      return state;
    },
    toggleMessagesVisible: (
      state,
      action: { payload: { visible: boolean; reportId?: string } },
    ) => {
      if (!action.payload.reportId) {
        state.messagesVisible = false;
        return state;
      }

      const caseById = state.caseById;
      const report = caseById?.Reports?.find(
        (report) => report.id === action.payload.reportId,
      );

      if (!report) {
        return state;
      }

      const isMIReport = report.submissionMode === SUBMISSION_MODE.MCC;
      const isUnknownMIReport =
        isMIReport &&
        !report.anonymous &&
        !report.Reporter?.name &&
        !report.Reporter?.email &&
        !report.reporterExists;

      if (isUnknownMIReport) {
        return state;
      }

      const isVaultTalkReport =
        report.submissionMode === SUBMISSION_MODE.VAULT_TALK;
      const isNonMobileVaultTalk =
        isVaultTalkReport && report.PhoneLineType !== PhoneLineType.MOBILE;

      if (isNonMobileVaultTalk) {
        return state;
      }

      if (!report.AssociatedPermissions?.canViewReportMessages) {
        return state;
      }

      state.messagesVisible = action.payload.visible;
      return state;
    },
    updateSelectedCaseTeamMemberPermissions: (
      state,
      action: { payload: CasePermissionsType[] },
    ) => {
      state.selectedCaseTeamMemberPermissions = action.payload;
      return state;
    },
    updateCaseTeamMemberPermissions: (
      state,
      action: {
        payload: {
          permissions: CasePermissionsType[];
          action: AccessActionEnum;
        };
      },
    ) => {
      const { permissions, action: updateAction } = action.payload;
      const currentPermissions = state.selectedCaseTeamMemberPermissions || [];

      state.selectedCaseTeamMemberPermissions = updatePermissions(
        currentPermissions as CasePermissionsType[],
        permissions,
        updateAction,
      );
      return state;
    },
    caseLinksUpdated(
      state,
      action: { payload: GetCaseLinksByCaseIdQuery['getCaseLinksByCaseId'] },
    ) {
      if (!action.payload) {
        return state;
      }
      state.caseLinks = action.payload;
      return state;
    },
  },

  selectors: {
    getSelectedCategories: (state) => {
      const { caseById } = state;
      const { canViewCaseDetails } = caseById?.AssociatedPermissions ?? {};
      if (!canViewCaseDetails) {
        return [{ id: 0, name: hiddenDots }];
      }
      return (
        caseById?.Categories?.map((category) => ({
          id: category.id,
          name: category.name,
        })) ?? []
      );
    },
    getSelectedTags: (state) => {
      const { caseById } = state;
      const { canViewCaseDetails } = caseById?.AssociatedPermissions ?? {};
      if (!canViewCaseDetails) {
        return [];
      }
      return caseById?.CaseTags ?? [];
    },
    casePageHeader: (
      state,
      globalState,
    ): {
      caseId: string;
      caseReadableId: string;
      namedOffenders: NamedOffenderForDisplay[];
      enableNamedOffenders: boolean;
      canEditCaseDetails: boolean;
      canViewCaseDetails: boolean;
      showCaseLinksSection: boolean;
      isImportedCase: boolean;
      importedCaseOriginalId?: string;
      isCaseSanitized: boolean;
    } => {
      const { caseById, caseLinks } = state;

      if (!caseById) {
        return {
          caseId: '',
          caseReadableId: '',
          namedOffenders: [],
          enableNamedOffenders: false,
          canEditCaseDetails: false,
          canViewCaseDetails: false,
          showCaseLinksSection: false,
          isImportedCase: false,
          importedCaseOriginalId: undefined,
          isCaseSanitized: false,
        };
      }

      const { canViewCaseDetailsPii, canEditCaseDetails, canViewCaseDetails } =
        caseById.AssociatedPermissions ?? {};

      const uniqueNamedOffenders = caseSlice.getSelectors().caseNamedOffenders(
        {
          ...state,
          translationModalOpenState: false,
          usersToExcludeInNamedOffenderModal: [],
        },
        globalState,
      );

      const showCaseLinksSection =
        canEditCaseDetails ||
        (canViewCaseDetails && (caseLinks ?? []).length > 0);

      const isImportedCase =
        !!caseById.importedId ||
        caseById.Reports?.[0]?.submissionMode === SUBMISSION_MODE.BULK_IMPORT ||
        caseById.Reports?.[0].source === REPORT_SOURCE.imported;

      return {
        caseId: caseById.id,
        caseReadableId: caseById.readableId,
        namedOffenders: uniqueNamedOffenders,
        enableNamedOffenders: canViewCaseDetailsPii
          ? uniqueNamedOffenders.length > 0
          : false,
        canEditCaseDetails: !!canEditCaseDetails,
        canViewCaseDetails: !!canViewCaseDetails,
        showCaseLinksSection: !!showCaseLinksSection,
        isImportedCase: isImportedCase,
        importedCaseOriginalId: caseById.importedId ?? undefined,
        isCaseSanitized: caseById.sanitizedAt != null,
      };
    },
    caseNamedOffenders: (
      { caseById },
      globalState,
    ): NamedOffenderForDisplay[] => {
      if (!caseById) return [];
      const useNormalizedDataEnabled = isRequiredFeatureEnabled(
        globalState.features,
        FeatureFlag.USE_NORMALIZED_REPORT_DATA,
      );

      const namedOffenders = getCaseNamedOffenders(
        caseById,
        useNormalizedDataEnabled,
      );

      const formattedNamedOffenders =
        formatReportNamedOffendersForDisplay(namedOffenders);

      return formattedNamedOffenders;
    },
    caseSelectedTranslation: ({ caseSelectedTranslation }) => {
      return (
        (caseSelectedTranslation?.toString() as TranscriptLanguages) ??
        undefined
      );
    },
    getCaseTeam: ({ caseById }) => {
      return caseById?.CaseTeam ?? [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateReportAssignee.pending, (state, action) => {
      if (!state.caseById?.Reports) return state;
      const updatedReportAssignee =
        state.caseById.AllCaseLeads?.find(
          (manager) => manager.id === action.meta.arg?.newReportAssigneeId,
        ) ?? state.caseById.Reports[0].Assignee;
      const report = state.caseById.Reports.find(
        (report) => report.id === action.meta.arg.report,
      );
      if (!report) {
        return state;
      }

      const newCaseTeam = state.caseById.CaseTeam.filter(
        (teamMember) => teamMember.id !== report.Assignee?.id,
      );

      report.Assignee = updatedReportAssignee;

      state.caseById.CaseTeam = newCaseTeam;
    });

    builder.addCase(updateSubmissionDate.pending, (state, action) => {
      if (!state.caseById?.Reports) return state;
      const updatedSubmissionDate =
        action.meta.arg?.submittedAt ?? state.caseById.Reports[0].submittedAt;
      const report = state.caseById.Reports.find(
        (report) => report.id === action.meta.arg.report,
      );
      if (!report) {
        return state;
      }
      report.submittedAt = updatedSubmissionDate;
    });

    builder.addCase(updateDeterminedCategory.pending, (state, action) => {
      if (!state.caseById?.Reports) return state;

      const updatedDeterminedCategory =
        state.categories?.find(
          (category) => category.id === action.meta.arg?.determinedCategoryId,
        ) ?? state.caseById.Reports[0].DeterminedCategory;

      const report = state.caseById.Reports.find(
        (report) => report.id === action.meta.arg.report,
      );
      if (!report) {
        return state;
      }
      report.DeterminedCategory = updatedDeterminedCategory;
    });

    builder.addCase(updateSubstantiationStatus.pending, (state, action) => {
      if (!state.caseById?.Reports) return state;

      const updatedSubstantiationStatus =
        action.meta.arg.newSubstantiationStatus ??
        state.caseById.Reports[0].substantiation;

      const report = state.caseById.Reports.find(
        (report) => report.id === action.meta.arg.report,
      );
      if (!report) {
        return state;
      }
      report.substantiation = updatedSubstantiationStatus;
    });

    builder.addCase(addOrLinkNamedOffender.fulfilled, (state, action) => {
      state.showAddNamedOffenderModal = false;
      state.usersToExcludeInNamedOffenderModal = [];
      state.selectedEventId = undefined;
      state.externalNamedOffenderToLink = undefined;
      handleSuccessToast({
        toastMessage: action.payload.message,
      });
    });

    builder.addCase(addOrLinkNamedOffender.rejected, (_, action) => {
      if (action.meta.arg.externalNamedOffenderToLink) {
        handleErrorToast({
          toastMessage: 'Error linking named offender',
        });
      } else {
        handleErrorToast({
          toastMessage: 'Error adding named offender',
        });
      }
    });
    builder.addCase(removeCaseTeamMember.fulfilled, (state) => {
      state.showRemoveCaseMemberModal = false;
      return state;
    });
    builder.addCase(editCaseTeamMemberAccess.fulfilled, (state) => {
      state.showEditCaseMemberModal = false;
      return state;
    });
  },
});

export interface CasePageVm {
  caseId: string;
  organisationId: number;
  caseLeadId?: number;
  permissions: {
    canViewCase: boolean;
    canViewCaseAuditLogsTab: boolean;
    canViewCaseWorkspaceTab: boolean;
    canEditCaseWorkspaceTab: boolean;
  };
  reportId: string;
  showAddNamedOffenderModal: boolean;
  usersToExcludeInNamedOffenderModal: number[];
  selectedEventId?: string;
  externalNamedOffenderToLink?: string;
  caseSelectedTranslation?: TranscriptLanguages;
  translationModalOpenState?: boolean;
  redirect?: boolean;
  categories: CategoryDisplayType[];
  caseDocuments?: Nullable<CaseDocumentationType[]>;
  auditEntries?: Nullable<AuditEntryType[]>;
  messagesVisible: boolean;
  showNewTeamMemberModal: boolean;
}

export interface RemovePIIReportEvents {
  eventId: string;
  event: string;
  reportId: string;
  namedOffenders: FormattedNamedOffender[];
  nonSeparatedExternalNamedOffenders: FormattedNamedOffender[];
  evidence?: Maybe<ParsedEvidenceType>;
  files?: FileEvidenceForDataDeletionType[];
}

export const casePageVm = createSelector(
  [
    (state: State) => state.case,
    (state: State) => state.globalUsers.currentUser,
  ],
  (caseState, currentUser): CasePageVm => {
    const { caseById, messagesVisible } = caseState;

    const isCaseLead =
      String(caseById?.CaseLead?.id) === String(currentUser?.id);

    const isReportAssignee = caseById?.Reports?.find(
      (r) => String(r.Assignee?.id) === String(currentUser?.id),
    );

    if (!caseById)
      return {
        caseId: '',
        permissions: {
          canViewCase: false,
          canViewCaseAuditLogsTab: false,
          canViewCaseWorkspaceTab: false,
          canEditCaseWorkspaceTab: false,
        },
        organisationId: -1,
        reportId: '',
        showAddNamedOffenderModal: false,
        usersToExcludeInNamedOffenderModal: [],
        translationModalOpenState: false,
        redirect: false,
        categories: [],
        messagesVisible: false,
        showNewTeamMemberModal: false,
      };

    const canViewCase = Boolean(
      caseById?.AssociatedPermissions &&
      caseById?.AssociatedPermissions?.canViewCase,
    );

    const canViewCaseAuditLogsTab = Boolean(
      caseById?.AssociatedPermissions?.canViewCaseAuditLogsTab,
    );

    const canViewCaseWorkspaceTab = Boolean(
      caseById?.AssociatedPermissions?.canViewCaseWorkspaceTab,
    );

    const canEditCaseWorkspaceTab = Boolean(
      caseById?.AssociatedPermissions?.canEditCaseWorkspaceTab,
    );

    const currentTeamMemberDetails = caseById?.CaseTeam?.find(
      (user) => String(user.id) === String(currentUser?.id), // redux currentUser.id is returned as a string but typed as a number
    );
    const hideNewTeamMemberModal =
      isCaseLead ||
      Boolean(isReportAssignee) ||
      Boolean(currentTeamMemberDetails?.firstAccessedAt);

    return {
      caseId: caseById.id,
      organisationId: caseById.organisation,
      caseLeadId: caseById.CaseLead?.id,
      permissions: {
        canViewCase,
        canViewCaseAuditLogsTab,
        canViewCaseWorkspaceTab,
        canEditCaseWorkspaceTab,
      },
      reportId: caseById.Reports?.[0]?.id ?? '',
      showAddNamedOffenderModal: caseState.showAddNamedOffenderModal ?? false,
      usersToExcludeInNamedOffenderModal:
        caseState.usersToExcludeInNamedOffenderModal ?? [],
      selectedEventId: caseState.selectedEventId,
      externalNamedOffenderToLink: caseState.externalNamedOffenderToLink,
      caseSelectedTranslation:
        (caseState.caseSelectedTranslation?.toString() as TranscriptLanguages) ??
        undefined,
      translationModalOpenState: caseState.translationModalOpenState,
      redirect: !canViewCase,
      messagesVisible: messagesVisible ?? false,
      categories: caseById.Categories,
      showNewTeamMemberModal:
        !hideNewTeamMemberModal && Boolean(currentTeamMemberDetails),
    };
  },
);

export const casePageTitleBar = createSelector(
  [(state: State) => state.case, getOrganisationTags],
  (caseState, organisationTags) => {
    const { caseById } = caseState;

    if (!caseById) return {};

    const { canViewCaseDetails, canEditCaseDetails, canEditCaseWorkspaceTab } =
      caseById.AssociatedPermissions ?? {};

    const caseTags = caseById?.CaseTags;

    const description = caseById?.description ?? '';

    const descriptionLastUpdated = formatDateWithSlashAndShortYear(
      caseById?.descriptionUpdatedAt,
    );

    const isImportedCase =
      !!caseById.importedId ||
      caseById.Reports?.[0]?.submissionMode === SUBMISSION_MODE.BULK_IMPORT ||
      caseById.Reports?.[0].source === REPORT_SOURCE.imported;
    const importedCaseOriginalId = caseById.importedId ?? undefined;

    return {
      caseId: caseById?.id,
      caseTitle: caseById?.name,
      caseTags,
      permissions: {
        canViewCaseDetails,
        canEditCaseDetails,
        canEditCaseWorkspaceTab,
      },
      organisationTags,
      description,
      descriptionLastUpdated,
      importedCaseOriginalId,
      isImportedCase,
    };
  },
);

export const caseStatusAndCategoriesPanel = createSelector(
  [(state: State) => state.case, getActiveCategories],
  (caseState, activeCategories) => {
    const { caseById } = caseState;

    if (!caseById)
      return {
        caseId: '',
        status: '',
        selectedCategories: [],
        activeCategories: [],
        permissions: {},
        selectedCountryCode: null,
        selectedRegionId: null,
      };

    const selectedCategories = caseSlice
      .getSelectors()
      .getSelectedCategories(caseState);

    const categoryOptions = activeCategories?.map((category) => ({
      name: category.name,
      id: category.id,
    }));

    const { canViewCaseDetails, canEditCaseDetails } =
      caseById.AssociatedPermissions ?? {};

    const ccr = canViewCaseDetails ? caseById.CaseCountryRegion : null;

    return {
      caseId: caseById.id,
      status: caseById.status,
      permissions: {
        canEditCaseDetails,
        canViewCaseDetails,
      },
      selectedCategories: selectedCategories.toSorted(sortObjectByName) ?? [],
      activeCategories: canViewCaseDetails
        ? (categoryOptions?.toSorted(sortObjectByName) ?? [])
        : [{ name: hiddenDots, id: 0 }],
      selectedCountryCode: ccr?.country?.code ?? null,
      selectedRegionId: ccr?.region?.id ?? null,
    };
  },
);

export const inviteToCaseModal = createSelector(
  [(state: State) => state.case],
  (caseState) => {
    const { caseById } = caseState;

    if (!caseById)
      return {
        caseId: '',
        readableCaseId: '',
        availableTeamMembers: [],
      };

    const allAvailableTeamMembers =
      caseById?.AllCaseTeamMembers?.map((member: UserType) => ({
        label: member.name || 'Unnamed User',
        value: member.id,
        role: member.role,
        site: member.site,
        country: member.country,
        isSuperAdmin: (member.Accounts ?? member.accounts)?.some(
          (account: AccountType) =>
            account.Roles?.some(
              // eslint-disable-next-line max-nested-callbacks
              (role: RoleType) =>
                role.SystemPermissions?.some(
                  // eslint-disable-next-line max-nested-callbacks
                  (permission) => permission.permission === 'EDIT_ALL_CASES',
                ) &&
                role.SystemPermissions?.some(
                  // eslint-disable-next-line max-nested-callbacks
                  (permission) => permission.permission === 'LIST_ALL_CASES',
                ),
            ),
        ),
      })) ?? [];

    const caseTeamMembersWithCaseTeamFlag: InviteUserDropdownOption[] =
      allAvailableTeamMembers.map((teamMember) => {
        const memberIsCaseTeamMember = caseById.CaseTeam?.some(
          (caseMember) => caseMember.id === teamMember.value,
        );

        return {
          ...teamMember,
          teamMember: Boolean(memberIsCaseTeamMember),
        };
      });

    const availableMembersNotOnCaseTeam =
      caseTeamMembersWithCaseTeamFlag.filter((member) => {
        const memberIsReportAssignee = caseById.Reports?.find(
          (report) => report.Assignee?.id === member.value,
        );
        const memberIsCaseLead = caseById.CaseLead?.id === member.value;
        return !memberIsReportAssignee && !memberIsCaseLead;
      });

    const report = caseById.Reports?.[0] as ReportType;
    const unableToChat = getMessagingDisabled(report);

    return {
      readableCaseId: caseById.readableId,
      caseId: caseById.id,
      availableTeamMembers: availableMembersNotOnCaseTeam,
      unableToChat,
    };
  },
);

export type FileEvidenceForDataDeletionType = {
  size: number | undefined;
  createdAt: Date;
  status: UploadScanStatus;
  fileExtension: string;
  fileS3Path: string;
  isPreSubmission: boolean;
  directFileUrl?: string;
  dataDeletedAt?: string | null;
};

const getPostSubmissionFiles = (
  report: ReportType,
): FileEvidenceForDataDeletionType[] => {
  return (
    report.postSubmissionFiles?.map((file) => {
      const defaultScanStatus = UploadScanStatus.CLEAN;
      const scanStatus = file.scanStatus as UploadScanStatus;
      const status = scanStatus ?? defaultScanStatus;

      return {
        size: file.size ?? undefined,
        createdAt: new Date(file.createdAt),
        status,
        fileExtension: file.fileExtension,
        fileS3Path: file.s3path,
        directFileUrl: `/api/portal/blobs/${report.id}/${file.s3path}`,
        isPreSubmission: false,
        dataDeletedAt: file.dataDeletedAt ?? null,
      };
    }) ?? []
  );
};

const getPreSubmissionFiles = (
  files: FileEvidenceDetailsType[] | undefined | null,
): FileEvidenceForDataDeletionType[] => {
  return (files || []).map((file) => {
    const defaultScanStatus = UploadScanStatus.CLEAN;
    const scanStatus = file.scanStatus as UploadScanStatus;
    const status = scanStatus ?? defaultScanStatus;

    return {
      size: file.fileSize ?? undefined,
      createdAt: new Date(file.createdAt),
      status,
      fileExtension:
        file.value.fileExtension ?? file.value.path.split('.')[1] ?? '',
      fileS3Path: stripFileExtension(file.value.path),
      isPreSubmission: true,
      dataDeletedAt: file.dataDeletedAt ?? null,
    };
  });
};

export const removePIIDataModal = (state: State) => {
  const { caseById } = state.case;

  if (!caseById)
    return {
      caseId: '',
      reportId: '',
      reportEvents: [],
      isReporterAnonymous: true,
      isReporterKnown: false,
      reportSource: REPORT_SOURCE.unknown,
      reporterNotes: '',
    };

  const reportEvents = caseById?.Reports?.flatMap(
    (report) => report.Evidence?.Events,
  );
  const useNormalizedDataEnabled = isRequiredFeatureEnabled(
    state.features,
    FeatureFlag.USE_NORMALIZED_REPORT_DATA,
  );

  const reports = caseById?.Reports;
  const allReportEvents: RemovePIIReportEvents[] = [];

  reports?.forEach((report) => {
    report.Evidence?.Events?.forEach((event) => {
      // eslint-disable-next-line max-nested-callbacks
      const reportEvent = report.ReportEvents?.find((e) => e.id === event.id);

      const { namedOffenders } = getEventNamedOffenders(
        event,
        report,
        useNormalizedDataEnabled,
        reportEvent,
      );

      let nonSeparatedExternalNamedOffenders: FormattedNamedOffender[] = [];
      let remainingNamedOffenders: FormattedNamedOffender[] = [];

      // Helper function to check if a string contains commas
      const containsComma = (str: string | undefined | null): boolean => {
        return typeof str === 'string' && str.includes(',');
      };

      const externalPreSubNamedOffenders = namedOffenders.filter(
        (offender) =>
          offender.external &&
          // eslint-disable-next-line max-nested-callbacks
          offender.events.find((ev) => ev.type === 'Presubmission'),
      );
      const reportIsVaultTalk = report.source === 'vault-talk';

      const showExternalNamedOffendersInTextBox =
        reportIsVaultTalk ||
        (externalPreSubNamedOffenders.length === 1 &&
          containsComma(externalPreSubNamedOffenders[0].name));

      nonSeparatedExternalNamedOffenders = showExternalNamedOffendersInTextBox
        ? externalPreSubNamedOffenders
        : [];

      remainingNamedOffenders = namedOffenders.filter(
        (offender) =>
          !nonSeparatedExternalNamedOffenders.some(
            // eslint-disable-next-line max-nested-callbacks
            (external) => external.id === offender.id,
          ),
      );

      const preSubmissionFiles = getPreSubmissionFiles(event?.evidence?.photo);

      allReportEvents.push({
        eventId: getReadableIdWithDash(event.id),
        event: event.id,
        reportId: report.id,
        namedOffenders: remainingNamedOffenders,
        nonSeparatedExternalNamedOffenders,
        evidence: event.evidence,
        files: preSubmissionFiles,
      });
    });
  });

  let postSubmissionFiles: FileEvidenceForDataDeletionType[] = [];

  reports?.forEach((report) => {
    postSubmissionFiles = postSubmissionFiles.concat(
      getPostSubmissionFiles(report),
    );
  });

  const externalPerpetrators = reportEvents?.flatMap((event) => {
    if (
      event?.evidence?.perpetrator &&
      event?.evidence?.perpetrator.length > 0
    ) {
      return event.evidence.perpetrator.filter(
        (perpetrator) => perpetrator.value.external,
      );
    }
    return [];
  });

  const isReporterAnonymous = caseById?.Reports?.some(
    (report) => report.anonymous,
  );

  const isReporterKnown = caseById?.Reports?.some(
    (report) =>
      !report.anonymous && (report.Reporter?.name || report.Reporter?.email),
  );

  const reportSource = caseById?.Reports?.[0]?.source;

  const reporterNotes = caseById?.Reports?.[0]?.ReportNotes?.notes;

  return {
    caseId: caseById.id,
    reportEvents: allReportEvents,
    postSubmissionFiles,
    externalPerpetrators,
    isReporterAnonymous,
    isReporterKnown,
    reportId: caseById.Reports?.[0]?.id ?? '',
    reportSource,
    reporterNotes,
  };
};

export type ReportPermissions = {
  canViewCase: boolean;
  canEditReportDetails: boolean;
  canViewReportDetails: boolean;
  canViewReportDetailsPii: boolean;
  canSendReportMessages: boolean;
  canViewReportMessages: boolean;
  canDownloadReport: boolean;
  canAdministerReportPermissions: boolean;
  canUpdateRelationshipToOrg: boolean;
};

export type ReportDetailsVm = {
  caseId: string;
  formattedReportId: string;
  subTabPanelKey: string;
  reportedTo: DefaultOption;
  submissionDate: string;
  reportedCategory: Nullable<DefaultOption>;
  determinedCategory: Nullable<DefaultOption>;
  substantiationStatus: DefaultOption;
  miReportSource: string;
  submissionRoute: string;
  submissionDateVariant: FormState;
  categories: DefaultOption[];
  relationshipTypes: DefaultOption[];
  determinedCategories: DefaultOption[];
  assignableReportManagers: UserType[];
  organisationId: number;
  permissions: ReportPermissions;
  events: DecryptedEvidenceType['Events'];
  caseLeadId: number;
  report?: ReportType;
  isAnonymous: boolean;
  reporter?: Reporter;
  notificationEmailState: FormState;
  namedOffenders: NamedOffenderForDisplay[];
  enableNamedOffenders: boolean;
  notes?: string;
  caseSelectedTranslation?: Nullable<TranscriptLanguages>;
  messagingDisabled: boolean;
  reportAssigneeState: FormState;
  viewerIsCaseLead?: boolean;
  updateRelationshipToOrgEnabled: boolean;
  relationshipType: Nullable<DefaultOption>;
  unreadMessagesCount?: number;
  isCaseSanitized: boolean;
};

type ReportDetailsSelectorArgs = {
  reportId: ReportType['id'];
  testVm?: ReportDetailsVm;
};

const selectReportDetailsVm = createSelector(
  [
    (state: State) => state.case,
    (state: State) => state.features,
    (state: State) => state.globalUsers,
    (_state: State, args: ReportDetailsSelectorArgs) => args.reportId,
    (_state: State, args: ReportDetailsSelectorArgs) => args.testVm,
  ],
  (caseState, features, globalUsers, reportId, testVm) => {
    if (testVm) return testVm;

    const state = {
      case: caseState,
      features,
      globalUsers,
    } as unknown as State;
    const currentUser = state.globalUsers.currentUser;

    const defaultViewModel: ReportDetailsVm = {
      caseId: '',
      formattedReportId: '',
      subTabPanelKey: `${reportId}-report-details-key`,
      reportedTo: { label: '', value: '' },
      submissionDate: '',
      reportedCategory: null,
      determinedCategory: null,
      substantiationStatus: { label: '', value: '' },
      miReportSource: '',
      submissionRoute: '',
      submissionDateVariant: FormState.DEFAULT,
      categories: [],
      relationshipTypes: [],
      determinedCategories: [],
      assignableReportManagers: [],
      organisationId: 0,
      permissions: {
        canViewCase: false,
        canEditReportDetails: false,
        canViewReportDetails: false,
        canViewReportDetailsPii: false,
        canSendReportMessages: false,
        canViewReportMessages: false,
        canDownloadReport: false,
        canAdministerReportPermissions: false,
        canUpdateRelationshipToOrg: false,
      },
      events: [],
      caseLeadId: 0,
      report: undefined,
      isAnonymous: false,
      reporter: undefined,
      notificationEmailState: FormState.DEFAULT,
      namedOffenders: [],
      enableNamedOffenders: false,
      notes: '',
      messagingDisabled: true,
      reportAssigneeState: FormState.DEFAULT,
      updateRelationshipToOrgEnabled: false,
      relationshipType: null,
      unreadMessagesCount: 0,
      isCaseSanitized: false,
    };

    const { caseById } = state.case;
    if (!caseById) return defaultViewModel;

    const report = caseById.Reports?.find((r) => r.id === reportId);
    if (!report) return defaultViewModel;

    const {
      canViewReportDetails,
      canEditReportDetails,
      canViewReportDetailsPii,
      canViewReportMessages,
      canAdministerReportPermissions,
    } = report.AssociatedPermissions ?? {};

    const submissionRoute = mapSubmissionModeToRoute(report);
    const getImportedMiReportSource = (report: ReportType) =>
      report.miReportSource ? report.miReportSource : '';

    const miReportSource =
      report.submissionMode === SUBMISSION_MODE.BULK_IMPORT
        ? getImportedMiReportSource(report)
        : (getReadableReportSource(
            report.miReportSource as MiReportSource,
          ) as MiReportSourceForDisplay);
    const viewerIsAssignee =
      String(report.Assignee?.id) === String(currentUser?.id);
    const viewerIsCaseLead =
      String(caseById.CaseLead?.id) === String(currentUser?.id);
    const isAnonymised = Boolean(report?.anonymous);
    const reportRelationshipToCompany = state.case.relationshipTypes?.find(
      (rt) => rt.id === report.Evidence?.relationshipToCompany,
    );

    const updateRelationshipToOrgEnabled = isRequiredFeatureEnabled(
      state.features,
      FeatureFlag.RELATIONSHIP_TO_ORG,
    );
    const reporter = getReporterDetails({
      reporter: report?.Reporter,
      isAnonymised,
      reportSource: report.source,
      piiPermission: canViewReportDetailsPii,
      reportRelationshipToCompany: updateRelationshipToOrgEnabled
        ? reportRelationshipToCompany?.name
        : undefined,
      updateReportRelationshipToOrgEnabled: updateRelationshipToOrgEnabled,
    });

    const canUpdateRelationshipToOrg =
      updateRelationshipToOrgEnabled &&
      canEditReportDetails &&
      canViewReportDetails &&
      canViewReportDetailsPii;

    const nonEditableNotificationEmail = isNonEditableNotificationEmail(report);

    const messagingDisabled = getMessagingDisabled(report);

    const notificationEmailState = getNotificationEmailState(
      nonEditableNotificationEmail,
      canEditReportDetails,
      canViewReportDetailsPii,
    );
    const useNormalizedDataEnabled = isRequiredFeatureEnabled(
      state.features,
      FeatureFlag.USE_NORMALIZED_REPORT_DATA,
    );
    const namedOffenders = getReportNamedOffenders(
      report,
      useNormalizedDataEnabled,
    );

    const formattedNamedOffenders =
      formatEventNamedOffendersForDisplay(namedOffenders);

    const categories = mapCategoriesToDefaultOptions(
      state.case.categories ?? [],
    );
    const relationshipTypes = mapCategoriesToDefaultOptions(
      state.case.relationshipTypes ?? [],
    );

    const reporterRelationshipToCompany =
      report?.Reporter?.relationshipToCompany;
    const relationshipToCompanyToUse =
      reportRelationshipToCompany?.id ?? reporterRelationshipToCompany;
    const selectedRelationshipType = relationshipToCompanyToUse
      ? (relationshipTypes.find(
          (rt) => rt.value === relationshipToCompanyToUse,
        ) ?? { value: 0, label: '—' })
      : { value: 0, label: '—' };

    const canDownloadReport = isDownloadReportEnabled(
      report.AssociatedPermissions ?? null,
      caseById.AssociatedPermissions ?? null,
      messagingDisabled,
    );

    const reportAssigneeState =
      viewerIsCaseLead ||
      Boolean(
        caseById.AssociatedPermissions?.canAdministerCasePermissions &&
        canAdministerReportPermissions,
      )
        ? FormState.DEFAULT
        : FormState.READONLY;

    return {
      caseId: caseById.id,
      formattedReportId: `Report: ${report.readableId}`,
      subTabPanelKey: `${reportId}-report-details-key`,
      reportedTo: getInputFormat(report.Assignee, canViewReportDetails),
      submissionDate: getSubmissionDate(
        report.createdAt,
        report.submittedAt,
        canViewReportDetails,
      ),
      reportedCategory: getInputFormat(
        report.ReportedCategory,
        canViewReportDetails,
      ),
      determinedCategory: getInputFormat(
        report.DeterminedCategory,
        canViewReportDetails,
      ),
      substantiationStatus: getSubstantiationStatus(
        report.substantiation,
        canViewReportDetails,
      ),
      miReportSource: getMiReportSource(miReportSource, canViewReportDetails),
      submissionRoute: getSubmissionRoute(
        submissionRoute,
        canViewReportDetails,
      ),
      submissionDateVariant:
        report.source === 'manual-case-creation' &&
        report.submissionMode === SUBMISSION_MODE.MCC &&
        Boolean(caseById.AssociatedPermissions?.canEditCaseCriDates)
          ? FormState.DEFAULT
          : FormState.READONLY,
      categories,
      relationshipTypes,
      determinedCategories: mapCategoriesToDefaultOptions(
        state.case.determinedCategories ?? [],
      ),
      assignableReportManagers: caseById.AllCaseLeads,
      organisationId: caseById.organisation,
      permissions: {
        canViewCase: viewerIsAssignee || viewerIsCaseLead,
        canDownloadReport,
        canViewReportMessages,
        canAdministerReportPermissions,
        canUpdateRelationshipToOrg,
        ...report.AssociatedPermissions,
      },
      events: canViewReportDetails
        ? (report.Evidence?.Events ?? [])
        : [{} as ParsedEventsType],
      caseLeadId: caseById.CaseLead?.id ?? 0,
      report,
      isAnonymous: Boolean(report?.anonymous),
      reporter,
      notificationEmailState,
      namedOffenders: canViewReportDetailsPii ? formattedNamedOffenders : [],
      enableNamedOffenders: canViewReportDetailsPii
        ? formattedNamedOffenders.length > 0
        : false,
      notes: getStringFormat(
        report.ReportNotes?.notes,
        canViewReportDetailsPii,
      ),
      caseSelectedTranslation:
        caseSlice.selectors.caseSelectedTranslation(state),
      messagingDisabled,
      reportAssigneeState,
      viewerIsCaseLead,
      updateRelationshipToOrgEnabled,
      relationshipType: canViewReportDetailsPii
        ? selectedRelationshipType
        : { value: 0, label: hiddenDots },
      unreadMessagesCount: report.unreadMessagesCount,
      isCaseSanitized: caseById.sanitizedAt != null,
    };
  },
);

export const reportDetailsVm =
  (args: ReportDetailsSelectorArgs) => (state: State) =>
    selectReportDetailsVm(state, args);

export type EventDetailsVm = {
  formattedEventId: string;
  dateCreated: string;
  eventEvidence: ReturnType<typeof formatEventEvidence>;
  addNamedOffenderEnabled: boolean;
  submissionMode: number | undefined;
  dynamicTranslationsEnabled: boolean;
  showAudioRecordings: boolean;
  audioRecordings: CallRecordingUrlType[];
  canAddNamedOffender: boolean;
  namedOffenders: FormattedNamedOffender[];
  usersToExcludeInNamedOffenderModal: number[];
  showNamedOffenders: boolean;
  showAddNamedOffenderModal: boolean;
  showAddNamedOffenderButton: boolean;
  reportSource: REPORT_SOURCE;
  canViewReportDetails: boolean;
  noEvidenceUploadedMessage: string;
};

type EventDetailsSelectorArgs = {
  eventId: ParsedEventsType['id'];
  reportId: ReportType['id'];
  testVm?: EventDetailsVm;
};

const selectEventDetailsVm = createSelector(
  [
    (state: State) => state.case,
    (state: State) => state.features,
    (_state: State, args: EventDetailsSelectorArgs) => args.eventId,
    (_state: State, args: EventDetailsSelectorArgs) => args.reportId,
    (_state: State, args: EventDetailsSelectorArgs) => args.testVm,
  ],
  (caseState, features, eventId, reportId, testVm): EventDetailsVm => {
    if (testVm) return testVm;

    const defaultViewModel = {
      formattedEventId: `Event ID: ${hiddenDots}`,
      dateCreated: '',
      eventEvidence: formatEventEvidence(),
      addNamedOffenderEnabled: false,
      submissionMode: undefined,
      dynamicTranslationsEnabled: false,
      showAudioRecordings: false,
      audioRecordings: [],
      canAddNamedOffender: false,
      namedOffenders: [],
      usersToExcludeInNamedOffenderModal: [],
      showNamedOffenders: false,
      showAddNamedOffenderModal: false,
      showAddNamedOffenderButton: false,
      reportSource: REPORT_SOURCE.unknown,
      canViewReportDetails: false,
      noEvidenceUploadedMessage: hiddenDots,
    };

    const { caseById } = caseState;

    if (!caseById) return defaultViewModel as EventDetailsVm;

    const report = caseById.Reports?.find((r) => r.id === reportId);
    if (!report) return defaultViewModel;

    const {
      canViewReportDetails,
      canViewReportDetailsPii,
      canAddNamedOffender,
    } = report?.AssociatedPermissions ?? {};

    let event = report.Evidence?.Events?.find((e) => e.id === eventId);
    if (!event) return defaultViewModel;

    event = event ?? ({} as ParsedEventsType);

    const reportEvent = report.ReportEvents?.find((e) => e.id === eventId);

    const isVaultTalkEnabled = isRequiredFeatureEnabled(
      features,
      newCasePageFeatures.VAULT_TALK,
    );
    const addNamedOffenderEnabled = isRequiredFeatureEnabled(
      features,
      newCasePageFeatures.ADD_NAMED_OFFENDER,
    );

    const dynamicTranslationsEnabled = isRequiredFeatureEnabled(
      features,
      newCasePageFeatures.DYNAMIC_TRANSLATIONS,
    );
    const useNormalizedDataEnabled = isRequiredFeatureEnabled(
      features,
      FeatureFlag.USE_NORMALIZED_REPORT_DATA,
    );

    const eventEvidence = formatEventEvidence(
      event.evidence,
      Boolean(canViewReportDetails),
    );

    const { namedOffenders, internalOffenderIds } = getEventNamedOffenders(
      event,
      report,
      useNormalizedDataEnabled,
      reportEvent,
    );

    const caseLevelUsersToExcludeFromSearch = new Set<number | undefined>([
      report.Reporter?.id,
      caseById.CaseLead?.id,
      report?.Assignee?.id,
      ...(caseById.CaseTeam?.map((teamMember) => teamMember.id) ?? []),
    ]);

    const usersToExclude = [
      ...internalOffenderIds,
      ...caseLevelUsersToExcludeFromSearch,
    ].filter((user) => user !== undefined);

    return {
      formattedEventId: `Event ID: ${
        event.id ? getReadableIdWithDash(event.id) : hiddenDots
      }`,
      dateCreated: `Date Created: ${formatDateOrHidden(
        new Date(event.created).toLocaleDateString(i18next.language),
        Boolean(canViewReportDetails),
      )}`,
      eventEvidence,
      addNamedOffenderEnabled,
      submissionMode: report.submissionMode,
      dynamicTranslationsEnabled,
      showAudioRecordings:
        isVaultTalkEnabled &&
        !report.anonymous &&
        report.source === 'vault-talk',
      audioRecordings: report.AudioRecordings,
      canAddNamedOffender,
      namedOffenders: canViewReportDetailsPii ? namedOffenders : [],
      usersToExcludeInNamedOffenderModal: usersToExclude,
      showNamedOffenders: canViewReportDetailsPii,
      showAddNamedOffenderModal: caseState.showAddNamedOffenderModal ?? false,
      showAddNamedOffenderButton: Boolean(
        addNamedOffenderEnabled &&
        caseById.Reports?.[0].AssociatedPermissions?.canAddNamedOffender,
      ),
      reportSource: graphQLReportSourceToLocalReportSource(report.source),
      canViewReportDetails,
      noEvidenceUploadedMessage: canViewReportDetails
        ? 'No evidence uploaded'
        : hiddenDots,
    } as EventDetailsVm;
  },
);

export const eventDetailsVm =
  (args: EventDetailsSelectorArgs) =>
  (state: State): EventDetailsVm =>
    selectEventDetailsVm(state, args);

export type PostSubmissionEvidenceVm = {
  files: UploadedEvidenceFile[];
};

type PostSubmissionEvidenceSelectorArgs = {
  reportId: ReportType['id'];
};

const selectPostSubmissionEvidenceVm = createSelector(
  [
    (state: State) => state.case,
    (_state: State, args: PostSubmissionEvidenceSelectorArgs) => args.reportId,
  ],
  (caseState, reportId) => {
    const defaultViewModel: PostSubmissionEvidenceVm = {
      files: [],
    };

    const { caseById } = caseState;
    if (!caseById) return defaultViewModel;

    const report = caseById.Reports?.find((r) => r.id === reportId);
    if (!report) return defaultViewModel;

    const { canViewReportDetails } = report.AssociatedPermissions ?? {};

    const uploadedFiles =
      report.postSubmissionFiles?.map((file) => ({
        size: file.size,
        dateAdded: file.createdAt,
        status: file.scanStatus as UploadScanStatus,
        fileExtension: file.fileExtension,
        fileS3Path: file.s3path,
        directFileUrl: `/api/portal/blobs/${reportId}/${file.s3path}`,
        dataDeletedAt: file.dataDeletedAt,
      })) ?? [];

    return { files: canViewReportDetails ? uploadedFiles : [] };
  },
);

export const postSubmissionEvidenceVm =
  (args: PostSubmissionEvidenceSelectorArgs) => (state: State) =>
    selectPostSubmissionEvidenceVm(state, args);

export const {
  caseDataUpdated,
  caseTeamNoteUpdated,
  caseCategoriesUpdated,
  viewerDataUpdated,
  caseDeterminedCategoriesUpdated,
  toggleShowAddNamedOffenderModal,
  caseSelectedTranslationUpdated,
  translationModalOpenStateUpdated,
  toggleShowRemoveCaseMemberModal,
  toggleShowEditCaseMemberModal,
  reporterNotificationEmailUpdated,
  toggleMessagesVisible,
  updateSelectedCaseTeamMemberPermissions,
  relationshipTypesUpdated,
  updateCaseTeamMemberPermissions,
  caseLinksUpdated,
} = caseSlice.actions;

// Wrapper selectors that properly pass both slice state and global state.
// The inner slice selectors only read `features` off the global state, so we
// memoize on `state.case` + `state.features` (stable refs) rather than the
// whole root state, which would change on every dispatch and defeat memoization.
export const casePageHeader = createSelector(
  [(state: State) => state.case, (state: State) => state.features],
  (caseState, features) =>
    caseSlice.selectors.casePageHeader({ case: caseState }, {
      features,
    } as State),
);

export const caseNamedOffenders = createSelector(
  [(state: State) => state.case, (state: State) => state.features],
  (caseState, features) =>
    caseSlice.selectors.caseNamedOffenders({ case: caseState }, {
      features,
    } as State),
);

export const selectCaseSelectedTranslation = (
  state: State | Record<string, unknown>,
) =>
  caseSlice.selectors.caseSelectedTranslation(
    state as { case: CaseInitialState },
  );

export const caseReducer = caseSlice.reducer;

export const newCasePageFeatures = {
  DYNAMIC_TRANSLATIONS: FeatureFlag.DYNAMIC_TRANSLATIONS,
  ADD_NAMED_OFFENDER: FeatureFlag.ADD_NAMED_OFFENDER,
  VAULT_TALK: FeatureFlag.VAULT_TALK,
};

export const getCaseTeamMember = (
  state: State,
): CaseTeamMemberType | undefined => {
  const currentUser = selectCurrentUser(state);
  const caseTeamMembers = caseSlice.getSelectors().getCaseTeam(state.case);

  const caseTeamMember = caseTeamMembers.find(
    (member) => member.id === Number(currentUser?.id),
  );

  return caseTeamMember;
};
